import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Config, { priceForLanguage } from '../../lib/Config'
import { Language } from '../../lib/Enums/Language'
import { Sheet } from '../../lib/Types'
import Headline from '../Elements/Headline'
import Text from '../Elements/Text'
import Paywall from '../Paywall'
import { SheetType } from './lib/Enums/SheetType'
import { isPaymentOverdue } from './lib/Payment'

interface Props {
  sheet: Sheet
  show: boolean
}

// The article this links to is translated on WWW under the same
// translationKey ("hvad-er-et-regnskab"), but each locale has its own domain
// and slug — see Guide.tsx for the same pattern.
const WELCOME_ACCOUNTING_ARTICLE_SLUG: Record<Language, string> = {
  [Language.DANISH]: 'hvad-er-et-regnskab',
  [Language.GERMAN]: 'buchhaltung',
  [Language.ENGLISH]: 'accounts',
}

const WelcomeAccounting: FC<Props> = ({ sheet, show }) => {
  const { t, i18n } = useTranslation(['accounting', 'common'])
  const [showModal, setShowModal] = useState(show)
  const overdue = isPaymentOverdue(sheet)
  const price = priceForLanguage(Config.PRICE_ACCOUNTING, i18n.language)

  return (
    <Paywall
      show={showModal}
      onHide={() => setShowModal(false)}
      sheetType={SheetType.ACCOUNTING}
      sheetId={sheet.id}
      name={sheet.name || t('common:paywall.accounting')}
      twoStep={!overdue}
      secondaryButtonLabel={
        overdue ? undefined : t('accounting:welcome.getStarted')
      }
    >
      {overdue
        ? (
          <>
            <Headline>{t('accounting:welcome.trialEndedTitle')}</Headline>
            <Text className='my-6'>
              {t('accounting:welcome.trialEndedDescription', { price })}
            </Text>
          </>
        )
        : (
          <>
            <Headline>{t('accounting:welcome.heading')}</Headline>
            <Text className='mt-3'>{t('accounting:welcome.intro')}</Text>
            <Text className='mt-6'>
              {t('accounting:welcome.trial', { price })}
            </Text>
            <Text className='mt-3 mb-6'>
              {t('accounting:welcome.readMore')}{' '}
              <a
                className='underline'
                href={`${Config.WWW_DOMAIN[i18n.language as Language] ?? Config.WWW_DOMAIN[Language.DANISH]}/regnskab/${WELCOME_ACCOUNTING_ARTICLE_SLUG[i18n.language as Language] ?? WELCOME_ACCOUNTING_ARTICLE_SLUG[Language.DANISH]}`}
                target='_blank'
                rel='noreferrer'
              >
                {t('accounting:welcome.readMoreLink')}
              </a>
              .
            </Text>
          </>
        )}
    </Paywall>
  )
}

export default WelcomeAccounting
